import { KeyboardEvent, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  List,
  Typography,
} from "@mui/material";
import { format, endOfWeek, isWithinInterval, startOfWeek } from "date-fns";
import { CalendarDays, Clock3, MapPin, Search, X } from "lucide-react";
import { EventNotice } from "protogen/calendar_pb";
import Loading from "../common/Loading";

interface Props {
  open: boolean;
  onClose: () => void;
  onSearch: (query: string) => Promise<EventNotice[]>;
  onSelectEvent: (event: EventNotice) => void;
  familyNameByRef?: Record<string, string>;
}

const DEFAULT_EMPTY = "Search by title, description or location.";

export default ({
  open,
  onClose,
  onSearch,
  onSelectEvent,
  familyNameByRef,
}: Props) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<EventNotice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const emptyMessage = useMemo(() => {
    const trimmed = query.trim();
    if (hasSearched && trimmed.length > 0 && trimmed.length < 2) {
      return "Type at least 2 characters to search.";
    }
    if (hasSearched && trimmed && !results.length) {
      return "No events matched that search.";
    }
    return DEFAULT_EMPTY;
  }, [hasSearched, query, results]);

  const runSearch = async (searchQuery?: string) => {
    const trimmed = (searchQuery ?? query).trim();
    if (searchQuery !== undefined) {
      setQuery(searchQuery);
    }
    setHasSearched(true);
    if (!trimmed) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    if (trimmed.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      setResults(await onSearch(trimmed));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void runSearch();
    }
  };

  const handleQueryChange = (nextValue: string) => {
    setQuery(nextValue);
    if (!nextValue.trim()) {
      setResults([]);
      setHasSearched(false);
      setIsLoading(false);
    }
  };

  const parseDateString = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const now = new Date();
  const thisWeekRange = {
    start: startOfWeek(now, { weekStartsOn: 0 }),
    end: endOfWeek(now, { weekStartsOn: 0 }),
  };
  const [thisWeekResults, upcomingResults, pastResults] = useMemo(() => {
    const thisWeek: EventNotice[] = [];
    const upcoming: EventNotice[] = [];
    const past: EventNotice[] = [];
    for (const event of results) {
      const eventDate = event.allDay
        ? parseDateString(event.startDate)
        : new Date(Number(event.startSec) * 1000);
      if (eventDate < now) {
        past.push(event);
      } else if (isWithinInterval(eventDate, thisWeekRange)) {
        thisWeek.push(event);
      } else {
        upcoming.push(event);
      }
    }
    return [thisWeek, upcoming, past];
  }, [results]);

  const formatEventDateTime = (event: EventNotice): string => {
    if (event.allDay) {
      if (event.startDate === event.endDate || !event.endDate) {
        return `${format(
          parseDateString(event.startDate),
          "EEE, MMM d",
        )} • All day`;
      }
      return `${format(
        parseDateString(event.startDate),
        "EEE, MMM d",
      )} - ${format(parseDateString(event.endDate), "EEE, MMM d")} • All day`;
    }
    const start = new Date(Number(event.startSec) * 1000);
    const end = new Date(Number(event.endSec) * 1000);
    return `${format(start, "EEE, MMM d")} • ${format(
      start,
      "h:mma",
    )} - ${format(end, "h:mma")}`;
  };

  const dotColor = (event: EventNotice): string => {
    const source = event.familyRef || event.externalCalendarRef || event.title;
    let hash = 0;
    for (let i = 0; i < source.length; i += 1) {
      hash = (hash << 5) - hash + source.charCodeAt(i);
      hash |= 0;
    }
    const colors = ["#149E96", "#7F5AF0", "#3E8ED0", "#2F9E44", "#F08C00"];
    return colors[Math.abs(hash) % colors.length];
  };

  const renderSection = (label: string, events: EventNotice[]) => {
    if (!events.length) {
      return null;
    }
    return (
      <Box>
        <Typography
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: "#8A99AF",
          }}
        >
          {label}
        </Typography>
        <List disablePadding>
          {events.map((event) => {
            const familyName = familyNameByRef?.[event.familyRef];
            const topics = (event.topics || []).slice(0, 3);
            return (
              <Box
                key={`${event.eventRef}-${event.startSec}`}
                onClick={() => onSelectEvent(event)}
                sx={{
                  px: 2.5,
                  py: 1.8,
                  display: "flex",
                  gap: 1.8,
                  cursor: "pointer",
                  borderTop: "1px solid #E6EBF1",
                  "&:hover": { backgroundColor: "#F7FAFD" },
                }}
              >
                <Box
                  sx={{
                    width: 14,
                    display: "flex",
                    justifyContent: "center",
                    pt: 0.8,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "999px",
                      backgroundColor: dotColor(event),
                    }}
                  />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      color: "#14213D",
                    }}
                  >
                    {event.title}
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.7,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                      color: "#60738E",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                    >
                      {event.allDay ? (
                        <CalendarDays size={14} />
                      ) : (
                        <Clock3 size={14} />
                      )}
                      <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                        {formatEventDateTime(event)}
                      </Typography>
                    </Box>
                    {event.locationString && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                      >
                        <MapPin size={14} />
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {event.locationString}
                        </Typography>
                      </Box>
                    )}
                    {familyName && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                      >
                        <CalendarDays size={14} />
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {familyName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {!!topics.length && (
                    <Box
                      sx={{
                        mt: 1.2,
                        display: "flex",
                        gap: 0.8,
                        flexWrap: "wrap",
                      }}
                    >
                      {topics.map((topic) => (
                        <Chip
                          key={`${event.eventRef}-${topic}`}
                          label={topic}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: "11px",
                            color: "#62748B",
                            backgroundColor: "#E9EEF5",
                            borderRadius: "999px",
                            "& .MuiChip-label": { px: 1, fontWeight: 600 },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(96vw, 680px)",
          borderRadius: "20px",
          border: "1px solid #DCE5EE",
          boxShadow: "0 28px 80px rgba(18, 38, 63, 0.16)",
          overflow: "hidden",
          backgroundColor: "#F8FAFD",
        },
      }}
    >
      <DialogTitle sx={{ px: 2.8, py: 2.2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
          <IconButton
            aria-label="Search events"
            onClick={() => void runSearch()}
            sx={{
              width: 36,
              height: 36,
              color: "#8B9CB4",
            }}
          >
            <Search size={24} strokeWidth={2} />
          </IconButton>
          <InputBase
            autoFocus
            fullWidth
            placeholder="Search calendar events..."
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{
              fontSize: "15px",
              fontWeight: 500,
              color: "#1B263B",
              "& input::placeholder": {
                color: "#9AA8BB",
                opacity: 1,
              },
            }}
          />
          <IconButton
            onClick={onClose}
            aria-label="Close calendar search"
            sx={{
              width: 36,
              height: 36,
              border: "1px solid #D9E2ED",
              backgroundColor: "#F3F6FA",
              borderRadius: "999px",
              color: "#6F7F95",
              "&:hover": {
                backgroundColor: "#EAF0F7",
              },
            }}
          >
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box sx={{ height: "1.5px", backgroundColor: "#1AA39B" }} />
      <DialogContent
        sx={{ p: 0, minHeight: "320px", maxHeight: "78vh", overflow: "auto" }}
      >
        {isLoading ? (
          <Loading minHeight={null} />
        ) : (
          <>
            {!results.length ? (
              <Box
                sx={{
                  py: 9,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    bgcolor: "#E7F0EF",
                    color: "#138E87",
                  }}
                >
                  <Search size={24} />
                </Avatar>
                <Typography
                  sx={{ fontSize: "18px", fontWeight: 700, color: "#1F2A44" }}
                >
                  Search calendar events
                </Typography>
                <Typography sx={{ fontSize: "14px", color: "#8A9AB0" }}>
                  {emptyMessage}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ pb: 1 }}>
                {renderSection("THIS WEEK", thisWeekResults)}
                {renderSection("UPCOMING", upcomingResults)}
                {renderSection("PAST", pastResults)}
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
